import { ClientInputError, ClientWalletError } from '@/lib/errors/client';

import { CHAIN_IDS } from '@/constants/chain';

import { submitLimitOrderCore } from '@/hooks/useSubmitLimitOrder';

import { getWalletClientAfterChainSwitch } from '@/utils/wallet';

import { getAdvancedOrderExpiry, getReusableSignableBuild } from '../../advancedOrderQuote';
import { submitAdvancedOrder } from '../submitAdvancedOrder';
import type { TradeStrategy } from '../types';

/** Advanced-order build, sign, and orderbook submission strategy. */
export const advancedStrategy: TradeStrategy = {
  lane: 'advanced',
  preflight: (ctx) => {
    if (
      ctx.quote.source !== 'advanced' ||
      ctx.quote.status !== 'derived' ||
      !ctx.advanced ||
      !ctx.accountAddress
    ) {
      return null;
    }
    if (ctx.chainId === CHAIN_IDS.SOLANA) {
      return ctx.solanaWallet ? { chainFamily: 'solana' } : null;
    }
    return ctx.ethereumWallet ? { chainFamily: 'evm' } : null;
  },
  submit: async (ctx, prepared) => {
    const advanced = ctx.advanced;
    const accountAddress = ctx.accountAddress;
    const result = await submitAdvancedOrder({
      chainId: ctx.chainId,
      sellToken: ctx.sellToken,
      buyToken: ctx.buyToken,
      sellInput: ctx.sellInput,
      buyInput: { ...ctx.buyInput, decimal: advanced?.buyAmountDecimal ?? ctx.buyInput.decimal },
      orderType: advanced?.orderType ?? 'limit',
      matchaReferrer: ctx.referrer.id,
      validate: () => {
        if (!advanced || !accountAddress) {
          throw new ClientInputError('submitLimitOrder: missing advanced order context');
        }
      },
      refreshOpenLimitOrders: (identity) => {
        if (accountAddress) ctx.deps.refreshOpenLimitOrders(accountAddress, identity);
      },
      setRecentTransaction: () => ctx.deps.setRecentTransaction(true),
      submit: async () => {
        if (!advanced || !accountAddress) {
          throw new ClientInputError('submitLimitOrder: missing advanced order context');
        }
        // A fresh quoted payload pins the expiry it was built with; otherwise the relative expiry anchors on now.
        const reusableBuild = getReusableSignableBuild(advanced.estimate);
        const expiry = reusableBuild
          ? new Date(Number(reusableBuild.request.expiration) * 1000)
          : getAdvancedOrderExpiry(advanced.expiration, advanced.customExpiration);

        const variables = {
          chainId: ctx.chainId,
          maker: accountAddress,
          buyToken: ctx.buyToken,
          sellToken: ctx.sellToken,
          buyAmount: advanced.buyAmountDecimal,
          sellAmount: ctx.sellInput.decimal,
          expiry,
          orderType: advanced.orderType,
          triggerCrossPrice: advanced.triggerCrossPrice,
          reusableBuild,
        };
        switch (prepared.chainFamily) {
          case 'evm': {
            const client = await getWalletClientAfterChainSwitch(ctx.chainId);
            if (!client || !client.account) {
              throw new ClientWalletError(
                'Wallet connection error. Please try again or reconnect your wallet.',
              );
            }
            if (client.chain?.id !== ctx.chainId) {
              throw new ClientWalletError('Wallet client chain does not match the Advanced order');
            }
            return submitLimitOrderCore(variables, {
              chainFamily: 'evm',
              chainId: ctx.chainId,
              walletClient: client,
              publicClient: ctx.publicClient,
              setPhase: () => undefined,
            });
          }
          case 'solana':
            if (ctx.chainId !== CHAIN_IDS.SOLANA || !ctx.solanaWallet) {
              throw new ClientWalletError('Solana wallet connection is unavailable');
            }
            return submitLimitOrderCore(variables, {
              chainFamily: 'svm',
              chainId: CHAIN_IDS.SOLANA,
              solanaWallet: ctx.solanaWallet,
              signMessage: ctx.signSolanaMessage,
              signTransaction: ctx.signSolanaTransaction,
              setPhase: () => undefined,
            });
          default: {
            const exhaustiveFamily: never = prepared.chainFamily;
            return exhaustiveFamily;
          }
        }
      },
    });
    if (!result.success) return result;

    ctx.deps.resetLimitRate();
    ctx.deps.resetInputs();
    return {
      success: true,
      outcome: {
        kind: result.submission.provider === 'definitive' ? 'orderId' : 'orderHash',
        value: result.submission.orderIdentifier,
      },
    };
  },
};
